/**
 * Outbound dial from inside a meeting.
 *
 * Calls the backend proxy, which forwards to the SIP or H.323 gateway with the
 * server-held key — a gateway key never touches the browser. Mirrors the auth +
 * error handling of recordings.api.
 *
 * `dialSip` is the original SIP-only route and is kept as it was. `dialOut` is
 * the protocol-aware one (stage H7): it can resolve the protocol from the
 * Address Book, and it returns the call so progress can be followed.
 */

import { config } from '@/config/env'

const API = config.apiUrl

export class SipDialError extends Error {}

function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const session = (JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } })
      ?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

/** Dial a SIP URI and pull the answering party into this meeting. */
export async function dialSip(meetingId: string, to: string): Promise<void> {
  const token = appToken()
  if (!token) throw new SipDialError('Not signed in')

  const res = await fetch(`${API}/api/v1/meetings/${encodeURIComponent(meetingId)}/sip-dial`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ to }),
  })

  if (!res.ok) {
    let message = `Dial failed (${res.status})`
    try {
      const body = await res.json()
      message = body?.error?.message || body?.error || message
    } catch {
      /* keep the status-based fallback */
    }
    throw new SipDialError(message)
  }
}

export type DialProtocol = 'auto' | 'sip' | 'h323'

export interface DialOutCall {
  /** Present for H.323; the SIP gateway has no call records yet (its stage S2). */
  callId?: string
  status?: string
  destination?: string
  endReason?: string | null
  peerId?: string | null
  /** Which protocol actually carried the dial, after `auto` was resolved. */
  protocol?: 'sip' | 'h323'
}

/**
 * Dial someone into this meeting.
 *
 * Defaults to SIP, which is what every dial did before the protocol was wired
 * up, and what the dialog selects unless the caller picks H.323.
 *
 * `auto` is still accepted by the backend — it resolves the protocol from the
 * Address Book, preferring the one the endpoint is currently registered on —
 * but the dialog no longer offers it: choosing for the caller is not this
 * dialog's job. Pass it explicitly if a caller ever wants that behaviour.
 */
export async function dialOut(
  meetingId: string,
  destination: string,
  protocol: DialProtocol = 'sip',
): Promise<DialOutCall> {
  return request<DialOutCall>(`/api/v1/meetings/${encodeURIComponent(meetingId)}/dial-out`, {
    method: 'POST',
    body: JSON.stringify({ destination, protocol }),
  })
}

/** Dial-out calls for this meeting. H.323 only — see DialOutCall.callId. */
export async function listDialOut(
  meetingId: string,
): Promise<{ calls: DialOutCall[]; h323Available: boolean }> {
  return request(`/api/v1/meetings/${encodeURIComponent(meetingId)}/dial-out`, { method: 'GET' })
}

/** Cancel a call that has not been answered. */
export async function cancelDialOut(meetingId: string, callId: string): Promise<DialOutCall> {
  return request<DialOutCall>(
    `/api/v1/meetings/${encodeURIComponent(meetingId)}/dial-out/${encodeURIComponent(callId)}/cancel`,
    { method: 'POST' },
  )
}

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const token = appToken()
  if (!token) throw new SipDialError('Not signed in')

  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  })

  if (!res.ok) {
    let message = `Dial failed (${res.status})`
    try {
      const body = await res.json()
      message = body?.error?.message || body?.error || message
    } catch {
      /* keep the status-based fallback */
    }
    throw new SipDialError(message)
  }

  return (await res.json().catch(() => ({}))) as T
}
